import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, ShieldAlert, Lock, Users, Ban, UserX, AlertTriangle, UserCheck,
  HeartPulse, Apple, ArrowRight, Activity, FileLock, CheckCircle2, Loader2,
  Twitter, Linkedin,
} from 'lucide-react';
import axios from 'axios';
import SiteNav from '../components/SiteNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  'heart-pulse': HeartPulse,
  'shield-alert': ShieldAlert,
  users: Users,
  lock: Lock,
  ban: Ban,
  'user-x': UserX,
  'alert-triangle': AlertTriangle,
  'user-check': UserCheck,
  apple: Apple,
};

/**
 * /trust-safety — public-facing transparency page.
 * Shows LIVE numbers from /api/public/safety/stats so parents searching
 * "is HiBFF safe for my teen?" land on a page that proves it.
 */
const TrustSafety = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/public/safety/stats`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setData({ error: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-white" data-testid="trust-safety-page">
      <SiteNav />
      <div className="bg-white/5 border-b border-white/5 px-6 py-2">
        <div className="max-w-6xl mx-auto flex items-center gap-6 text-sm text-white/70">
          <Link to="/teen-safety" className="hover:text-white" data-testid="trust-safety-teen-link">For parents</Link>
          <Link to="/trust-center" className="hover:text-white" data-testid="trust-safety-center-link">For schools</Link>
          <Link to="/privacy" className="hover:text-white">Privacy</Link>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 opacity-30" style={{
          background: 'radial-gradient(800px 400px at 20% 20%, rgba(245,131,31,0.18), transparent 60%), radial-gradient(700px 400px at 80% 60%, rgba(99,102,241,0.18), transparent 60%)',
        }} />
        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/70 mb-6" data-testid="trust-safety-live-pill">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Live numbers — refreshed in real time
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight max-w-4xl leading-[1.05]">
            Safe by <span className="text-[#F5831F]">design</span>.<br />
            Proven by <span className="text-emerald-400">numbers</span>.
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-white/70 max-w-2xl">
            Seven-layer defence on every message, two independent moderation engines,
            and a public block-log a parent can audit at any time.
          </p>

          {/* Headline numbers */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="trust-safety-headline-grid">
            <StatTile
              label="Messages screened"
              value={loading ? null : data?.headline?.messages_screened}
              testid="stat-messages-screened"
            />
            <StatTile
              label="Total blocks"
              value={loading ? null : data?.headline?.total_blocks}
              accent="orange"
              testid="stat-total-blocks"
            />
            <StatTile
              label="Blocks · last 30 days"
              value={loading ? null : data?.headline?.blocks_last_30d}
              accent="emerald"
              testid="stat-blocks-30d"
            />
            <StatTile
              label="NeMo Guardrails blocks"
              value={loading ? null : data?.headline?.nemo_blocks_total}
              accent="indigo"
              testid="stat-nemo-blocks"
            />
          </div>
        </div>
      </section>

      {/* DEFENCE-IN-DEPTH */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#F5831F] mb-2">The stack</p>
            <h2 className="text-3xl sm:text-4xl font-black">Every message passes through 7 gates</h2>
          </div>
          <EngineBadge active={data?.engines?.nemo_guardrails?.active} />
        </div>

        <div className="grid md:grid-cols-2 gap-3" data-testid="trust-safety-layers-grid">
          {(data?.layers || []).map((l) => (
            <LayerRow key={l.n} layer={l} />
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-xs uppercase tracking-[0.2em] text-[#F5831F] mb-2">What we block</p>
          <h2 className="text-3xl sm:text-4xl font-black mb-10">9 harm categories, every message, every account.</h2>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="trust-safety-categories-grid">
            {(data?.categories || []).map((c) => {
              const Ico = CATEGORY_ICONS[c.icon] || ShieldCheck;
              return (
                <div
                  key={c.slug}
                  data-testid={`category-${c.slug}`}
                  className="flex items-start gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-colors"
                >
                  <Ico size={20} className="text-[#F5831F] mt-0.5 shrink-0" />
                  <span className="text-sm text-white/85">{c.label}</span>
                </div>
              );
            })}
          </div>

          {data?.by_reason && data.by_reason.length > 0 && (
            <div className="mt-12 p-6 rounded-3xl bg-white/[0.03] border border-white/5">
              <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">Live block ledger — what triggered each block</p>
              <div className="space-y-2" data-testid="trust-safety-by-reason">
                {data.by_reason.map((r) => (
                  <div key={r.reason} className="flex items-center justify-between text-sm">
                    <code className="text-white/70 font-mono text-xs">{r.reason}</code>
                    <div className="flex items-center gap-3 min-w-[200px]">
                      <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className="h-full bg-[#F5831F]"
                          style={{ width: `${Math.min(100, (r.count / data.headline.total_blocks) * 100)}%` }}
                        />
                      </div>
                      <span className="text-white/60 tabular-nums w-8 text-right">{r.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ENGINES & RETENTION */}
      <section className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-6">
        <div className="p-8 rounded-3xl bg-white/[0.03] border border-white/5" data-testid="engines-card">
          <Activity size={28} className="text-emerald-400 mb-4" />
          <h3 className="text-2xl font-black mb-3">Two independent moderation engines</h3>
          <p className="text-white/65 mb-6">
            We deliberately run two unrelated systems. If one misses something, the other catches it.
          </p>
          <EngineDetail
            name="OpenAI omni-moderation + custom blocklist"
            active={!!data?.engines?.safety_filter?.active}
            sub="MongoDB-managed keyword/phrase rules · OpenAI Moderation API"
          />
          <EngineDetail
            name="NVIDIA NeMo Guardrails"
            active={!!data?.engines?.nemo_guardrails?.active}
            sub={`gpt-4o-mini · input rails: ${(data?.engines?.nemo_guardrails?.input_rails || []).join(', ')} · output rails: ${(data?.engines?.nemo_guardrails?.output_rails || []).join(', ')}`}
          />
        </div>

        <div className="p-8 rounded-3xl bg-white/[0.03] border border-white/5" data-testid="retention-card">
          <FileLock size={28} className="text-indigo-400 mb-4" />
          <h3 className="text-2xl font-black mb-3">Auditable, retained, parent-accessible</h3>
          <p className="text-white/65 mb-6">{data?.retention?.summary}</p>
          <RetentionRow label="Block evidence" days={data?.retention?.block_evidence_days} />
          <RetentionRow label="Audit log" days={data?.retention?.audit_log_days} />
          <RetentionRow label="Teen messages" days={data?.retention?.teen_messages_days} />
          <p className="text-xs text-white/40 mt-6">
            Block evidence is the metadata (reason, category, timestamp, message excerpt). Teen
            conversation content auto-deletes after 90 days regardless.
          </p>
        </div>
      </section>

      {/* FRAMEWORKS */}
      <section className="max-w-6xl mx-auto px-6 py-12 border-t border-white/5">
        <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">Aligned with</p>
        <div className="flex flex-wrap gap-2" data-testid="trust-safety-frameworks">
          {(data?.frameworks || []).map((f) => (
            <span key={f} className="px-4 py-2 rounded-full bg-white/5 text-sm text-white/75 border border-white/5">{f}</span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 py-20 text-center border-t border-white/5">
        <h2 className="text-3xl sm:text-4xl font-black mb-4">Still got questions?</h2>
        <p className="text-white/65 mb-8 max-w-xl mx-auto">
          Read the plain-English parent guide, see our schools compliance pack, or talk to a human on our team.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/teen-safety"
            data-testid="cta-parent-guide"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#F5831F] text-black font-semibold hover:bg-[#FFA64D] transition-colors"
          >
            Parent guide <ArrowRight size={16} />
          </Link>
          <Link
            to="/trust-center"
            data-testid="cta-schools-pack"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
          >
            Schools compliance pack
          </Link>
          <Link
            to="/contact"
            data-testid="cta-contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
          >
            Talk to a human
          </Link>
        </div>
        <p className="mt-10 text-xs text-white/30" data-testid="trust-safety-asof">
          Numbers refresh on every page load · as of {data?.as_of?.split('T')[0] || '—'}
        </p>
      </section>

      {/* OFFICIAL CHANNELS FOOTER */}
      <footer className="border-t border-white/5 bg-white/[0.02]" data-testid="trust-safety-channels-footer">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="/hibff-logo.png" alt="HiBFF" className="h-8 opacity-80" />
            <span className="text-xs text-white/40">© HiBFF · Hi Brave Friends Forever</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Follow us</span>
            <div className="flex items-center gap-2">
              <a
                href="https://x.com/GoHiBFF"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-x-link"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              >
                <Twitter size={14} /> @GoHiBFF
              </a>
              <a
                href="https://www.linkedin.com/in/hibff/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin-link"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              >
                <Linkedin size={14} /> linkedin.com/in/hibff
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const StatTile = ({ label, value, accent, testid }) => {
  const accentColors = {
    orange: 'text-[#F5831F]',
    emerald: 'text-emerald-400',
    indigo: 'text-indigo-300',
  };
  return (
    <div className="p-6 rounded-2xl bg-white/[0.04] border border-white/5" data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.15em] text-white/40 mb-3">{label}</p>
      {value === null || value === undefined ? (
        <Loader2 className="animate-spin text-white/30" size={28} />
      ) : (
        <p className={`text-4xl sm:text-5xl font-black tabular-nums ${accentColors[accent] || 'text-white'}`}>
          {value.toLocaleString()}
        </p>
      )}
    </div>
  );
};

const LayerRow = ({ layer }) => (
  <div className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5" data-testid={`layer-${layer.n}`}>
    <div className="w-9 h-9 shrink-0 rounded-full bg-[#F5831F]/15 border border-[#F5831F]/30 flex items-center justify-center">
      <span className="text-[#F5831F] font-black text-sm">{layer.n}</span>
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-white">{layer.name}</p>
      <p className="text-xs text-white/55 mt-1 font-mono">{layer.engine}</p>
    </div>
    <div className="flex flex-col items-end gap-1 shrink-0">
      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
        layer.scope === 'input' ? 'border-emerald-400/30 text-emerald-300 bg-emerald-400/5' :
        layer.scope === 'output' ? 'border-indigo-400/30 text-indigo-300 bg-indigo-400/5' :
        'border-white/15 text-white/60 bg-white/5'
      }`}>{layer.scope}</span>
      {layer.latency_ms != null && (
        <span className="text-[10px] text-white/40 font-mono">~{layer.latency_ms}ms</span>
      )}
    </div>
  </div>
);

const EngineBadge = ({ active }) => (
  <div
    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border ${
      active ? 'bg-emerald-400/10 border-emerald-400/30 text-emerald-300' : 'bg-white/5 border-white/10 text-white/40'
    }`}
    data-testid="trust-safety-nemo-badge"
  >
    <span className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-400 animate-pulse' : 'bg-white/30'}`} />
    NeMo Guardrails {active ? 'LIVE' : 'OFFLINE'}
  </div>
);

const EngineDetail = ({ name, active, sub }) => (
  <div className="flex items-start gap-3 py-3 border-t border-white/5 first:border-t-0">
    <CheckCircle2 size={18} className={active ? 'text-emerald-400 mt-0.5 shrink-0' : 'text-white/20 mt-0.5 shrink-0'} />
    <div className="min-w-0">
      <p className="font-semibold text-white">{name}</p>
      <p className="text-xs text-white/50 mt-0.5">{sub}</p>
    </div>
  </div>
);

const RetentionRow = ({ label, days }) => (
  <div className="flex items-center justify-between py-3 border-t border-white/5 first:border-t-0">
    <span className="text-white/75">{label}</span>
    <span className="text-white/90 tabular-nums font-mono text-sm">{days ?? '—'} days</span>
  </div>
);

export default TrustSafety;
